import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";

import { GroundingPayload, PassioFood, PassioNutrient } from "./types";

// React 17 (what the accounts app bundles) has no `useId`, so we mint stable,
// per-instance ids from a module counter instead (safe: assigned once on mount).
let __uidSeq = 0;
const useStableId = (prefix: string): string => {
  const [id] = useState(() => `${prefix}-${++__uidSeq}`);
  return id;
};

export const upcOf = (food?: PassioFood | null): string => {
  if (!food) return "";
  return (food.branded?.productCode || food.branded?.upc || "").toString();
};

export const countryOf = (food?: PassioFood | null): string =>
  (food?.branded?.country || "").toString();

// Deterministic JSON: sort object keys recursively so two records with the same
// content but different key order compare equal.
const stableStringify = (v: unknown): string => {
  if (v === null || typeof v !== "object") return JSON.stringify(v) ?? "null";
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`;
  const obj = v as Record<string, unknown>;
  return `{${Object.keys(obj)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`)
    .join(",")}}`;
};

/**
 * True when an edited draft actually differs from the original record, ignoring
 * key ordering. Used to block no-op saves (nothing edited → nothing to write).
 */
export const foodsChanged = (
  a?: PassioFood | null,
  b?: PassioFood | null
): boolean => stableStringify(a ?? null) !== stableStringify(b ?? null);

export const ingredientsOf = (food?: PassioFood | null): string =>
  (food?.branded?.ingredients || "").toString();

/** Immutably apply common branded/identity edits to a food record. */
export const applyFoodEdits = (
  food: PassioFood,
  edits: {
    name?: string;
    brandName?: string;
    upc?: string;
    country?: string;
    ingredients?: string;
    tags?: string[];
  }
): PassioFood => {
  const next: PassioFood = { ...food };
  if (edits.name !== undefined) next.name = edits.name;
  if (edits.brandName !== undefined) next.brandName = edits.brandName;
  if (edits.tags !== undefined) next.tags = edits.tags;

  const brandedTouched =
    edits.upc !== undefined ||
    edits.country !== undefined ||
    edits.ingredients !== undefined ||
    edits.brandName !== undefined;
  if (brandedTouched) {
    const branded = { ...(food.branded || {}) };
    if (edits.upc !== undefined) branded.productCode = edits.upc || undefined;
    if (edits.country !== undefined)
      branded.country = edits.country || undefined;
    if (edits.ingredients !== undefined)
      branded.ingredients = edits.ingredients || undefined;
    if (edits.brandName !== undefined && !branded.owner && edits.brandName)
      branded.owner = edits.brandName;
    next.branded = branded;
  }
  return next;
};

export const findNutrientAmount = (
  food: PassioFood | undefined,
  keys: string[]
): number | undefined => {
  if (!food?.nutrients) return undefined;
  const lowered = keys.map((k) => k.toLowerCase());
  const match = food.nutrients.find((n: PassioNutrient) => {
    const name = (n.nutrient?.name || "").toLowerCase();
    const short = (n.nutrient?.shortName || "").toLowerCase();
    return lowered.some((k) => name.includes(k) || short.includes(k));
  });
  return match?.amount;
};

const fmt = (v: number | undefined, unit: string): string =>
  v === undefined || Number.isNaN(v)
    ? "—"
    : `${Math.round(v * 10) / 10}${unit}`;

/** Compact per-100g macro line for a food. */
export const MacroLine: React.FC<{ food?: PassioFood }> = ({ food }) => {
  const kcal = findNutrientAmount(food, ["energy", "calorie"]);
  const carbs = findNutrientAmount(food, ["carbohydrate", "carb"]);
  const protein = findNutrientAmount(food, ["protein"]);
  const fat = findNutrientAmount(food, ["total fat", "fat", "lipid"]);
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
      <span>Cals {fmt(kcal, "")}</span>
      <span>Carbs {fmt(carbs, "g")}</span>
      <span>Protein {fmt(protein, "g")}</span>
      <span>Fat {fmt(fat, "g")}</span>
      <span className="text-gray-400">/ 100g</span>
    </div>
  );
};

export const Badge: React.FC<{
  tone?: "gray" | "green" | "blue" | "red" | "amber";
  children: React.ReactNode;
}> = ({ tone = "gray", children }) => {
  const tones: Record<string, string> = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-100 text-green-700",
    blue: "bg-blue-100 text-blue-700",
    red: "bg-red-100 text-red-700",
    amber: "bg-amber-100 text-amber-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${tones[tone]}`}
    >
      {children}
    </span>
  );
};

export const Spinner: React.FC<{ label?: string }> = ({ label }) => (
  <span className="inline-flex items-center gap-2 text-sm text-gray-600">
    <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600" />
    {label}
  </span>
);

/** Centered modal dialog with a dimmed backdrop. */
export const Modal: React.FC<{
  open: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Footer actions (buttons). */
  footer?: React.ReactNode;
  /** Hide the ✕ / disable backdrop-close (e.g. while a job is running). */
  dismissable?: boolean;
  tone?: "default" | "error";
}> = ({
  open,
  onClose,
  title,
  children,
  footer,
  dismissable = true,
  tone = "default",
}) => {
  if (!open) return null;
  // Portal to <body> so the backdrop covers the whole viewport (a transformed
  // ancestor would otherwise become the containing block for `fixed`, leaving
  // an unshaded strip at the top of the page).
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-gray-900/50"
        onClick={() => dismissable && onClose?.()}
      />
      <div className="relative z-10 w-full max-w-lg rounded-xl bg-white shadow-2xl">
        {(title || dismissable) && (
          <div
            className={`flex items-start justify-between gap-3 rounded-t-xl border-b px-5 py-3 ${
              tone === "error"
                ? "border-red-100 bg-red-50"
                : "border-gray-100 bg-white"
            }`}
          >
            <h3
              className={`text-base font-semibold ${
                tone === "error" ? "text-red-800" : "text-gray-900"
              }`}
            >
              {title}
            </h3>
            {dismissable && (
              <button
                type="button"
                onClick={onClose}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Close"
              >
                ✕
              </button>
            )}
          </div>
        )}
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="flex justify-end gap-2 rounded-b-xl border-t border-gray-100 bg-gray-50 px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

/**
 * Shared bottom-right stack that ALL floating alerts portal into (every `Toast`
 * plus the background-runs tray), so they stack vertically instead of piling up
 * in the same corner and covering each other. The box is anchored at the bottom
 * and grows UPWARD as children are added; the runs tray pins itself to the
 * bottom (via `order-last`) and transient toasts stack above it. `pointer-events`
 * are off on the container (so the gaps never block clicks) and back on for each
 * child.
 */
export const getAlertStackContainer = (): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  let el = document.getElementById("cm-alert-stack") as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = "cm-alert-stack";
    el.className =
      "pointer-events-none fixed bottom-4 right-4 z-[1100] flex max-h-[calc(100vh-2rem)] flex-col items-end gap-2";
    document.body.appendChild(el);
  }
  return el;
};

/** Tailwind classes for one item sitting in the shared alert stack. */
export const ALERT_STACK_ITEM_CLASS =
  "pointer-events-auto w-[22rem] max-w-[calc(100vw-2rem)]";

/**
 * Floating toast that stacks in the shared bottom-right alert area. Grabbier
 * than an inline banner — use it for "don't miss this" reminders (e.g. a merge
 * still needs a write). Auto-dismisses after `duration` ms (set 0 to keep it
 * sticky).
 */
export const Toast: React.FC<{
  open: boolean;
  message: React.ReactNode;
  tone?: "info" | "success" | "warn";
  actionLabel?: string;
  onAction?: () => void;
  onClose: () => void;
  duration?: number;
}> = ({
  open,
  message,
  tone = "info",
  actionLabel,
  onAction,
  onClose,
  duration = 7000,
}) => {
  useEffect(() => {
    if (!open || !duration) return undefined;
    const t = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(t);
  }, [open, duration, onClose]);

  if (!open) return null;

  const tones: Record<string, string> = {
    info: "border-indigo-400 bg-indigo-600",
    success: "border-green-400 bg-green-600",
    warn: "border-amber-300 bg-amber-500",
  };

  const container = getAlertStackContainer();
  if (!container) return null;

  return ReactDOM.createPortal(
    <div
      className={`${ALERT_STACK_ITEM_CLASS} animate-[fadeIn_0.15s_ease-out]`}
    >
      <div
        className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-white shadow-2xl ${
          tones[tone] || tones.info
        }`}
        role="status"
        aria-live="polite"
      >
        <div className="flex-1 text-sm font-medium leading-snug">{message}</div>
        {actionLabel && onAction && (
          <button
            type="button"
            onClick={() => {
              onAction();
              onClose();
            }}
            className="flex-shrink-0 rounded-md bg-white/20 px-2.5 py-1 text-xs font-semibold text-white hover:bg-white/30"
          >
            {actionLabel}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="flex-shrink-0 text-lg leading-none text-white/70 hover:text-white"
        >
          ✕
        </button>
      </div>
    </div>,
    container
  );
};

export const JsonBlock: React.FC<{
  value: unknown;
  label?: string;
  defaultOpen?: boolean;
}> = ({ value, label = "JSON", defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-semibold text-indigo-600 hover:text-indigo-800"
      >
        {open ? "▾ Hide" : "▸ Show"} {label}
      </button>
      {open && (
        <pre className="mt-1 max-h-72 overflow-auto rounded-md bg-gray-900 p-3 text-xs leading-relaxed text-gray-100">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
};

/**
 * Editable tag chips. When `allowed` is provided, the input only accepts values
 * from that controlled list (type-to-search via a datalist); free text that
 * isn't in the list is rejected with a short hint.
 */
export const TagEditor: React.FC<{
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  /** Controlled vocabulary; when set, only these tags can be added. */
  allowed?: string[];
}> = ({ tags, onChange, disabled, allowed }) => {
  const [draft, setDraft] = useState("");
  const [hint, setHint] = useState("");
  const listId = useStableId("tag-list");

  const canonical = (raw: string): string | null => {
    const t = raw.trim();
    if (!t) return null;
    if (!allowed) return t;
    const hit = allowed.find((a) => a.toLowerCase() === t.toLowerCase());
    return hit ?? null;
  };

  const add = (raw: string) => {
    const t = canonical(raw);
    if (!t) {
      if (raw.trim() && allowed)
        setHint(`"${raw.trim()}" isn't an allowed tag`);
      return;
    }
    if (!tags.includes(t)) onChange([...tags, t]);
    setDraft("");
    setHint("");
  };

  const suggestions = allowed
    ? allowed.filter(
        (a) => !tags.some((t) => t.toLowerCase() === a.toLowerCase())
      )
    : [];

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {tags.length === 0 && (
          <span className="text-xs text-gray-400">No tags</span>
        )}
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
          >
            {t}
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(tags.filter((x) => x !== t))}
                className="text-indigo-400 hover:text-indigo-700"
                aria-label={`Remove ${t}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {!disabled && (
        <>
          <div className="mt-2 flex gap-2">
            <input
              value={draft}
              list={allowed ? listId : undefined}
              onChange={(e) => {
                setDraft(e.target.value);
                setHint("");
                // Selecting a datalist option fires change with the full value;
                // auto-add when it's an exact allowed match.
                if (allowed && canonical(e.target.value)) add(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add(draft);
                }
              }}
              placeholder={allowed ? "Add tag — type to search…" : "Add tag…"}
              className="w-56 rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-indigo-500 focus:outline-none"
            />
            {allowed && (
              <datalist id={listId}>
                {suggestions.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            )}
            <button
              type="button"
              onClick={() => add(draft)}
              className="rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200"
            >
              Add
            </button>
          </div>
          {hint && <p className="mt-1 text-[11px] text-amber-600">{hint}</p>}
        </>
      )}
    </div>
  );
};

export const PrimaryButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className = "", children, ...rest }) => (
  <button
    {...rest}
    className={`inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

export const SecondaryButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement>
> = ({ className = "", children, ...rest }) => (
  <button
    {...rest}
    className={`inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

/**
 * Expandable, display-only web-search grounding panel. Renders the search
 * queries, source links, and the full grounded answer captured during the
 * research step. This data is review-only and is never sent to later steps.
 */
export const GroundingPanel: React.FC<{
  grounding?: GroundingPayload | null;
}> = ({ grounding }) => {
  const [open, setOpen] = useState(false);
  if (!grounding) return null;
  const queries = grounding.web_search_queries || [];
  const sources = grounding.sources || [];
  const hasContent =
    queries.length > 0 || sources.length > 0 || !!grounding.answer;
  if (!hasContent) return null;

  return (
    <div className="mt-3 rounded-md border border-sky-200 bg-sky-50/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-sky-800"
      >
        <span>{open ? "▾" : "▸"}</span>
        <span>🔎 Web search details</span>
        <span className="font-normal text-sky-600">
          {sources.length > 0
            ? `${grounding.source_count ?? sources.length} source${
                sources.length === 1 ? "" : "s"
              }`
            : "grounded"}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-sky-200 px-3 py-2">
          {queries.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600">
                Search queries
              </div>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {queries.map((q, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-700"
                  >
                    {q}
                  </span>
                ))}
              </div>
            </div>
          )}
          {sources.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-600">
                Sources ({grounding.source_count ?? sources.length})
              </div>
              <ol className="mt-1 list-decimal space-y-0.5 pl-5">
                {sources.map((s, i) => (
                  <li key={i} className="text-xs">
                    {s.uri ? (
                      <a
                        href={s.uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        {s.title || s.uri}
                      </a>
                    ) : (
                      <span className="text-gray-700">{s.title}</span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
          {grounding.answer && (
            <div>
              <div className="text-xs font-semibold text-gray-600">
                Full grounded answer{" "}
                <span className="font-normal text-gray-400">
                  (not passed downstream)
                </span>
              </div>
              <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-gray-200 bg-white p-2 text-xs leading-relaxed text-gray-700">
                {grounding.answer}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
